// Écran des actions sociales (lot L7, M20) : activation explicite par le maire ou président, synthèse agrégée
// (effectifs masqués sous cinq), puis, pour les seuls comptes sociaux habilités, programmes et demandes avec
// recherche et export. Sans habilitation, aucune donnée individuelle : l'écran le dit, sans rien simuler.
import type { ProgrammeSocial, ResumeDemandeSociale } from '@cc/contrats';
import { ETATS_DEMANDE_SOCIALE, ETATS_PROGRAMME_SOCIAL, TYPES_AIDE_SOCIALE } from '@cc/contrats';
import {
  Bandeau,
  Bouton,
  Champ,
  EtatVide,
  Lien,
  PuceEtat,
  Selection,
  Tableau,
  type ColonneTableau,
} from '@cc/ui';
import { HeartHandshake } from 'lucide-react';
import { useState } from 'react';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession } from '../../session/FournisseurSession';
import { peut, voitDomaine } from '../../session/permissions';
import { dateCourte } from '../urbanisme/PageDemandes';
import { exporterDemandes, useActivation, useDemandes, useProgrammes, useSynthese } from './api';
import { FormulaireActivation, FormulaireDemande, FormulaireProgramme } from './FormulairesSocial';
import { effectif, libelleEtat, montant, TON_ETAT_DEMANDE } from './logique';
import styles from '../achats/achats.module.css';

type Dialogue = 'activation' | 'programme' | 'demande' | null;

export function PageSocial() {
  const { contexte } = useSession();
  const habilite = voitDomaine(contexte, 'social');
  const activation = useActivation();
  const synthese = useSynthese();
  const [filtre, setFiltre] = useState({ programme_id: '', etat: '', recherche: '' });
  const programmes = useProgrammes(habilite);
  const demandes = useDemandes(
    {
      programme_id: filtre.programme_id || undefined,
      etat: filtre.etat || undefined,
      recherche: filtre.recherche || undefined,
    },
    habilite,
  );
  const [d, setD] = useState<Dialogue>(null);
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const actif = activation.data?.actif === true;
  const colonnesProgrammes: ColonneTableau<ProgrammeSocial>[] = [
    {
      id: 'code',
      entete: 'Programme',
      enTeteLigne: true,
      masquable: false,
      cellule: (p) => p.intitule,
    },
    { id: 'type', entete: 'Aide', cellule: (p) => TYPES_AIDE_SOCIALE[p.type_aide] },
    { id: 'etat', entete: 'État', cellule: (p) => ETATS_PROGRAMME_SOCIAL[p.etat] },
    {
      id: 'criteres',
      entete: 'Critères',
      cellule: (p) =>
        `version ${p.version_courante.version} (${p.version_courante.criteres.length} critères)`,
    },
    {
      id: 'enveloppe',
      entete: 'Enveloppe indicative',
      numerique: true,
      cellule: (p) => montant(p.enveloppe_indicative, p.fictif),
    },
    { id: 'demandes', entete: 'Demandes', numerique: true, cellule: (p) => String(p.demandes) },
  ];
  const colonnesDemandes: ColonneTableau<ResumeDemandeSociale>[] = [
    {
      id: 'numero',
      entete: 'Demande',
      enTeteLigne: true,
      masquable: false,
      cellule: (x) => (
        <Lien href={`/social/demandes/${x.id}`} className="cc-mono">
          {x.numero}
        </Lien>
      ),
      valeurTri: (x) => x.numero,
    },
    { id: 'beneficiaire', entete: 'Bénéficiaire', cellule: (x) => x.beneficiaire },
    { id: 'programme', entete: 'Programme', cellule: (x) => x.programme.intitule },
    {
      id: 'recue',
      entete: 'Reçue le',
      cellule: (x) => dateCourte(x.recue_le),
      valeurTri: (x) => x.recue_le,
    },
    {
      id: 'doublons',
      entete: 'Doublons à examiner',
      numerique: true,
      cellule: (x) => String(x.doublons_a_examiner),
    },
    {
      id: 'etat',
      entete: 'État',
      cellule: (x) => <PuceEtat ton={TON_ETAT_DEMANDE[x.etat]} libelle={libelleEtat(x.etat)} />,
    },
  ];
  const exporter = () => {
    setErreurExport(null);
    exporterDemandes({
      programme_id: filtre.programme_id || undefined,
      etat: filtre.etat || undefined,
    }).catch(() =>
      setErreurExport("L'export n'a pas pu être produit (droit d'export et habilitation requis)."),
    );
  };
  return (
    <>
      <EnTetePage
        etiquette="Actions sociales"
        titre="Actions sociales"
        description="Programmes d'aide, demandes, instruction, décisions motivées et suivi. Module optionnel, activé seulement par le maire ou président ; chaque donnée individuelle exige un rôle social et une habilitation nominative."
        actions={
          <>
            {habilite && actif && peut(contexte, 'social', 'creer') ? (
              <Bouton variante="principal" onClick={() => setD('demande')}>
                Nouvelle demande
              </Bouton>
            ) : null}
            {habilite && actif && peut(contexte, 'social', 'administrer') ? (
              <Bouton variante="secondaire" onClick={() => setD('programme')}>
                Nouveau programme
              </Bouton>
            ) : null}
            {habilite && peut(contexte, 'social', 'exporter') ? (
              <Bouton variante="secondaire" onClick={exporter}>
                Exporter (CSV)
              </Bouton>
            ) : null}
          </>
        }
      />
      {erreurExport ? <Bandeau type="erreur">{erreurExport}</Bandeau> : null}
      <section className={styles.bloc} aria-labelledby="titre-activation">
        <h2 id="titre-activation">Activation du module</h2>
        <EtatRequete
          chargement={activation.isPending}
          erreur={activation.error}
          onReessayer={() => void activation.refetch()}
          lot="L7"
          fonction="L'état du module"
        >
          <div className={styles.actes}>
            <PuceEtat
              ton={actif ? 'ok' : 'neutre'}
              libelle={actif ? 'Module actif' : 'Module désactivé'}
            />
            <span className={styles.detail}>
              {actif
                ? `Activé le ${dateCourte(activation.data?.active_le ?? null)}${activation.data?.motif ? ` : ${activation.data.motif}` : ''}.`
                : 'Aucune nouvelle demande ne peut être enregistrée ; les demandes existantes restent consultables sous habilitation.'}
            </span>
            {activation.data?.peut_modifier ? (
              <Bouton variante="secondaire" onClick={() => setD('activation')}>
                {actif ? 'Désactiver' : 'Activer'}
              </Bouton>
            ) : (
              <span className={styles.detail}>
                Seul le maire ou président active ou désactive ce module.
              </span>
            )}
          </div>
        </EtatRequete>
      </section>
      <section className={styles.bloc} aria-labelledby="titre-synthese">
        <h2 id="titre-synthese">Synthèse (agrégats)</h2>
        <EtatRequete
          chargement={synthese.isPending}
          erreur={synthese.error}
          onReessayer={() => void synthese.refetch()}
          lot="L7"
          fonction="La synthèse sociale"
        >
          {(synthese.data?.programmes.length ?? 0) === 0 ? (
            <p className={styles.detail}>Aucun programme enregistré.</p>
          ) : (
            <Tableau
              legende={`Effectifs par programme, masqués sous ${synthese.data?.seuil_masquage ?? 5}`}
              colonnes={[
                {
                  id: 'programme',
                  entete: 'Programme',
                  enTeteLigne: true,
                  masquable: false,
                  cellule: (p) => p.intitule,
                },
                {
                  id: 'demandes',
                  entete: 'Demandes',
                  numerique: true,
                  cellule: (p) => effectif(p.demandes),
                },
                {
                  id: 'en_cours',
                  entete: 'En cours',
                  numerique: true,
                  cellule: (p) => effectif(p.en_cours),
                },
                {
                  id: 'accordees',
                  entete: 'Accordées',
                  numerique: true,
                  cellule: (p) => effectif(p.accordees),
                },
                {
                  id: 'refusees',
                  entete: 'Refusées',
                  numerique: true,
                  cellule: (p) => effectif(p.refusees),
                },
              ]}
              lignes={synthese.data?.programmes ?? []}
              cleLigne={(p) => p.code}
              cleColonnes="social-synthese"
            />
          )}
        </EtatRequete>
      </section>
      {!habilite ? (
        <Bandeau type="info">
          Les programmes et les demandes individuelles exigent un rôle social et une habilitation
          nominative « social » en vigueur, accordée et validée par deux personnes distinctes. Votre
          rôle seul n&apos;y donne pas accès.
        </Bandeau>
      ) : (
        <>
          <section className={styles.bloc} aria-labelledby="titre-programmes">
            <h2 id="titre-programmes">Programmes</h2>
            <EtatRequete
              chargement={programmes.isPending}
              erreur={programmes.error}
              onReessayer={() => void programmes.refetch()}
              lot="L7"
              fonction="La liste des programmes"
            >
              {(programmes.data?.elements.length ?? 0) === 0 ? (
                <p className={styles.detail}>Aucun programme.</p>
              ) : (
                <Tableau
                  legende="Programmes d'actions sociales"
                  colonnes={colonnesProgrammes}
                  lignes={programmes.data?.elements ?? []}
                  cleLigne={(p) => p.id}
                  cleColonnes="social-programmes"
                />
              )}
            </EtatRequete>
          </section>
          <section className={styles.bloc} aria-labelledby="titre-demandes">
            <h2 id="titre-demandes">Demandes</h2>
            <div className={styles.grille}>
              <Champ
                libelle="Rechercher (numéro ou nom)"
                value={filtre.recherche}
                onChange={(e) => setFiltre((f) => ({ ...f, recherche: e.target.value }))}
                facultatif
              />
              <Selection
                libelle="Programme"
                options={(programmes.data?.elements ?? []).map((p) => ({
                  valeur: p.id,
                  libelle: p.intitule,
                }))}
                optionVide="Tous"
                value={filtre.programme_id}
                onChange={(e) => setFiltre((f) => ({ ...f, programme_id: e.target.value }))}
                facultatif
              />
              <Selection
                libelle="État"
                options={Object.entries(ETATS_DEMANDE_SOCIALE).map(([valeur, libelle]) => ({
                  valeur,
                  libelle,
                }))}
                optionVide="Tous"
                value={filtre.etat}
                onChange={(e) => setFiltre((f) => ({ ...f, etat: e.target.value }))}
                facultatif
              />
            </div>
            <EtatRequete
              chargement={demandes.isPending}
              erreur={demandes.error}
              onReessayer={() => void demandes.refetch()}
              lot="L7"
              fonction="La liste des demandes"
            >
              {(demandes.data?.elements.length ?? 0) === 0 ? (
                <EtatVide titre="Aucune demande" icone={HeartHandshake}>
                  <p>Aucune demande ne correspond.</p>
                </EtatVide>
              ) : (
                <Tableau
                  legende="Demandes d'aide sociale"
                  colonnes={colonnesDemandes}
                  lignes={demandes.data?.elements ?? []}
                  cleLigne={(x) => x.id}
                  cleColonnes="social-demandes"
                />
              )}
            </EtatRequete>
          </section>
        </>
      )}
      {d === 'activation' && activation.data ? (
        <FormulaireActivation actif={actif} onFermer={() => setD(null)} />
      ) : null}
      {d === 'programme' ? <FormulaireProgramme onFermer={() => setD(null)} /> : null}
      {d === 'demande' ? (
        <FormulaireDemande
          programmes={(programmes.data?.elements ?? []).filter((p) => p.etat === 'ouvert')}
          onFermer={() => setD(null)}
        />
      ) : null}
    </>
  );
}
